<div class="tree-node">

  <!-- Node row -->
  <div class="node-row"
       [class.node-row--root]="isRoot"
       [class.node-row--editing]="isEditing"
       [class.node-row--details-open]="isDetailsOpen"
       [class.node-row--drag-over]="isDragOver"
       (dragover)="onDragOver($event)"
       (dragleave)="onDragLeave()"
       (drop)="onDrop($event)">

    <!-- Drag handle -->
    <button class="action-btn action-btn--drag"
            *ngIf="!isRoot"
            draggable="true"
            (dragstart)="onDragStart($event)"
            (dragend)="onDragEnd()"
            title="Drag to move node"
            [disabled]="isMoving">
      <fa-icon [icon]="faArrowsAlt"></fa-icon>
    </button>

    <!-- Expand / collapse toggle -->
    <button class="toggle-btn"
            *ngIf="hasChildren || isAddingChild; else toggleSpacer"
            (click)="isExpanded = !isExpanded"
            [title]="isExpanded ? 'Collapse' : 'Expand'">
      <fa-icon [icon]="isExpanded ? faChevronDown : faChevronRight"></fa-icon>
    </button>
    <ng-template #toggleSpacer>
      <span class="toggle-spacer"></span>
    </ng-template>

    <!-- Node name (static) or inline edit input -->
    <ng-container *ngIf="!isEditing; else editBlock">
      <span class="node-name" [class.node-name--root]="isRoot" (dblclick)="startEdit()" title="Double-click to rename">
        {{ levelPrefix }}{{ node.name }}
      </span>
    </ng-container>

    <ng-template #editBlock>
      <input #editInputEl
             class="edit-input"
             [(ngModel)]="editName"
             (keyup.enter)="saveEdit()"
             (keyup.escape)="cancelEdit()"
             placeholder="Node name"
             [disabled]="isSaving" />
    </ng-template>

    <!-- Action buttons -->
    <div class="node-actions">

      <!-- View mode actions -->
      <ng-container *ngIf="!isEditing">
        <button class="action-btn action-btn--edit"
                (click)="startEdit()"
                title="Rename node"
                [disabled]="isSaving">
          <fa-icon [icon]="faPencilAlt"></fa-icon>
        </button>

        <button class="action-btn action-btn--add"
                (click)="startAddChild()"
                title="Add child node"
                [disabled]="isSaving">
          <fa-icon [icon]="faPlus"></fa-icon>
        </button>

        <span *ngIf="!isRoot" [title]="deleteDisabledReason">
          <button class="action-btn action-btn--delete"
                  (click)="deleteNode()"
                  [disabled]="isSaving || hasLinkedAssociations"
                  [attr.aria-label]="deleteDisabledReason">
            <fa-icon [icon]="faTrash"></fa-icon>
          </button>
        </span>

        <span class="action-separator"></span>

        <button class="action-btn action-btn--details"
                [class.action-btn--details-active]="isDetailsOpen"
                (click)="toggleDetails()"
                title="Node details (asset &amp; devices)">
          <fa-icon [icon]="faCog"></fa-icon>
        </button>
      </ng-container>

      <!-- Edit mode actions -->
      <ng-container *ngIf="isEditing">
        <button class="action-btn action-btn--save"
                (click)="saveEdit()"
                title="Save"
                [disabled]="isSaving || !editName.trim()">
          <fa-icon [icon]="faCheck"></fa-icon>
        </button>
        <button class="action-btn action-btn--cancel"
                (click)="cancelEdit()"
                title="Cancel"
                [disabled]="isSaving">
          <fa-icon [icon]="faTimes"></fa-icon>
        </button>
      </ng-container>

    </div>
  </div>

  <!-- Details panel (GeoAsset + Devices) -->
  <div class="node-details" *ngIf="isDetailsOpen">

    <!-- Associated Asset section -->
    <div class="node-details__section">
      <div class="node-details__section-title">Associated Asset</div>

      <div class="node-details__field-row">
        <select class="node-details__select"
                [(ngModel)]="selectedGeoAssetId"
                [disabled]="isSavingDetails">
          <option [ngValue]="null">&mdash; None &mdash;</option>
          <option *ngFor="let asset of geoAssets" [ngValue]="asset.id">{{ asset.name }}</option>
        </select>
        <button class="action-btn action-btn--save"
                (click)="saveGeoAsset()"
                title="Save asset association"
                [disabled]="isSavingDetails">
          <fa-icon [icon]="faCheck"></fa-icon>
        </button>
      </div>

      <div class="node-details__hint" *ngIf="geoAssets.length === 0">
        No irrigatable assets found for this customer.
      </div>
    </div>

    <!-- Water Allowance section -->
    <div class="node-details__section">
      <div class="node-details__section-title">Annual Water Allowance</div>

      <div class="node-details__field-row">
        <div class="node-details__field-group">
          <label class="node-details__label">Allowance (kL)</label>
          <input class="node-details__input"
                 type="number"
                 min="0"
                 max="100000"
                 step="1"
                 placeholder="e.g. 5000"
                 [(ngModel)]="editWaterAllowance"
                 [disabled]="isSavingWaterAllowance" />
        </div>
        <div class="node-details__field-group">
          <label class="node-details__label">Reset Month</label>
          <select class="node-details__select"
                  [(ngModel)]="editAllowanceResetMonth"
                  [disabled]="isSavingWaterAllowance">
            <option [ngValue]="null">&mdash; None &mdash;</option>
            <option *ngFor="let m of months" [ngValue]="m.value">{{ m.label }}</option>
          </select>
        </div>
        <button class="action-btn action-btn--save"
                (click)="saveWaterAllowance()"
                title="Save water allowance"
                [disabled]="isSavingWaterAllowance">
          <fa-icon [icon]="faCheck"></fa-icon>
        </button>
        <button class="action-btn action-btn--cancel"
                (click)="editWaterAllowance = null; editAllowanceResetMonth = null"
                title="Clear water allowance"
                [disabled]="isSavingWaterAllowance">
          <fa-icon [icon]="faTimes"></fa-icon>
        </button>
      </div>

      <div class="node-details__warning" *ngIf="waterAllowanceWithoutMeter">
        <i class="fa fa-exclamation-triangle"></i>
        No water meter is linked to this node. Usage cannot be tracked without one.
      </div>
    </div>

    <!-- Linked Devices section -->
    <div class="node-details__section">
      <div class="node-details__section-title">Linked Devices</div>

      <!-- Current linked devices -->
      <div class="node-details__device-list" *ngIf="node.associatedDevices?.length">
        <div class="node-details__device" *ngFor="let device of node.associatedDevices">
          <span class="node-details__device-type">{{ device.deviceTypeName }}</span>
          <span class="node-details__device-uri">{{ device.uri }}</span>
          <button class="action-btn action-btn--delete"
                  (click)="unlinkDevice(device)"
                  title="Unlink device"
                  [disabled]="isSavingDetails">
            <fa-icon [icon]="faTimes"></fa-icon>
          </button>
        </div>
      </div>
      <div class="node-details__hint" *ngIf="!node.associatedDevices?.length && !isLinkingDevice">
        No devices linked to this node.
      </div>

      <!-- Link existing device -->
      <ng-container *ngIf="!isLinkingDevice">
        <button class="node-details__link-btn"
                (click)="startLinkDevice()"
                [disabled]="isSavingDetails">
          <fa-icon [icon]="faLink"></fa-icon> Link Device
        </button>
      </ng-container>

      <ng-container *ngIf="isLinkingDevice && !isAddingNewDevice">
        <div class="node-details__field-row" *ngIf="isLoadingDevices">
          <div class="node-details__spinner">
            <span class="nt-spinner"></span> Loading&hellip;
          </div>
        </div>
        <ng-container *ngIf="!isLoadingDevices">
          <div class="node-details__field-row">
            <select class="node-details__select"
                    [(ngModel)]="selectedDeviceId"
                    [disabled]="isSavingDetails">
              <option [ngValue]="null">&mdash; Select a device &mdash;</option>
              <option *ngFor="let d of unlinkedDevices" [ngValue]="d.id">
                {{ d.deviceTypeName }}{{ d.uri ? ' &mdash; ' + d.uri : '' }}
              </option>
            </select>
            <button class="action-btn action-btn--save"
                    (click)="linkDevice()"
                    title="Confirm link"
                    [disabled]="isSavingDetails || !selectedDeviceId">
              <fa-icon [icon]="faCheck"></fa-icon>
            </button>
            <button class="action-btn action-btn--cancel"
                    (click)="cancelLinkDevice()"
                    title="Cancel"
                    [disabled]="isSavingDetails">
              <fa-icon [icon]="faTimes"></fa-icon>
            </button>
          </div>
          <div class="node-details__hint" *ngIf="unlinkedDevices.length === 0">
            No unlinked devices available.
          </div>
          <button class="node-details__link-btn"
                  (click)="startAddNewDevice()"
                  [disabled]="isSavingDetails">
            <fa-icon [icon]="faPlus"></fa-icon> Add New Device
          </button>
        </ng-container>
      </ng-container>

      <!-- Add new device -->
      <ng-container *ngIf="isLinkingDevice && isAddingNewDevice">
        <div class="node-details__field-row" *ngIf="isLoadingDevices">
          <div class="node-details__spinner">
            <span class="nt-spinner"></span> Loading&hellip;
          </div>
        </div>
        <ng-container *ngIf="!isLoadingDevices">
          <div class="node-details__field-row">
            <div class="node-details__field-group">
              <label class="node-details__label">Type</label>
              <select class="node-details__select"
                      [(ngModel)]="selectedDeviceTypeId"
                      [disabled]="isSavingDetails">
                <option [ngValue]="null">&mdash; Select type &mdash;</option>
                <option *ngFor="let t of deviceTypes" [ngValue]="t.id">{{ t.name }}</option>
              </select>
            </div>
            <div class="node-details__field-group">
              <label class="node-details__label">URI</label>
              <input class="node-details__input"
                     type="text"
                     placeholder="Device URI"
                     [(ngModel)]="newDeviceUri"
                     [disabled]="isSavingDetails" />
            </div>
            <button class="action-btn action-btn--save"
                    (click)="addNewDevice()"
                    title="Add device"
                    [disabled]="isSavingDetails || !selectedDeviceTypeId">
              <fa-icon [icon]="faCheck"></fa-icon>
            </button>
            <button class="action-btn action-btn--cancel"
                    (click)="cancelAddNewDevice()"
                    title="Back"
                    [disabled]="isSavingDetails">
              <fa-icon [icon]="faTimes"></fa-icon>
            </button>
          </div>
        </ng-container>
      </ng-container>
    </div>

    <!-- Associated Users section -->
    <div class="node-details__section">
      <div class="node-details__section-title">Associated Users</div>

      <!-- Current linked users -->
      <div class="node-details__device-list" *ngIf="node.associatedUsers?.length">
        <div class="node-details__device" *ngFor="let nodeUser of node.associatedUsers">
          <fa-icon [icon]="faUser" class="node-details__device-type"></fa-icon>
          <span class="node-details__device-type">{{ nodeUser.userDisplayName }}</span>
          <button class="action-btn action-btn--delete"
                  (click)="unlinkUser(nodeUser)"
                  title="Unlink user"
                  [disabled]="isSavingDetails">
            <fa-icon [icon]="faTimes"></fa-icon>
          </button>
        </div>
      </div>
      <div class="node-details__hint" *ngIf="!node.associatedUsers?.length && !isLinkingUser">
        No users associated with this node.
      </div>

      <!-- Link new user -->
      <ng-container *ngIf="!isLinkingUser">
        <button class="node-details__link-btn"
                (click)="startLinkUser()"
                [disabled]="isSavingDetails">
          <fa-icon [icon]="faUser"></fa-icon> Link User
        </button>
      </ng-container>

      <div class="node-details__field-row" *ngIf="isLinkingUser">
        <div class="node-details__spinner" *ngIf="isLoadingUsers">
          <span class="nt-spinner"></span> Loading&hellip;
        </div>
        <ng-container *ngIf="!isLoadingUsers">
          <select class="node-details__select"
                  [(ngModel)]="selectedUserId"
                  [disabled]="isSavingDetails">
            <option [ngValue]="null">&mdash; Select a user &mdash;</option>
            <option *ngFor="let u of associatableUsers" [ngValue]="u.userId">
              {{ u.displayName || u.firstName + ' ' + u.lastName }}
            </option>
          </select>
          <button class="action-btn action-btn--save"
                  (click)="linkUser()"
                  title="Confirm link"
                  [disabled]="isSavingDetails || !selectedUserId">
            <fa-icon [icon]="faCheck"></fa-icon>
          </button>
          <button class="action-btn action-btn--cancel"
                  (click)="cancelLinkUser()"
                  title="Cancel"
                  [disabled]="isSavingDetails">
            <fa-icon [icon]="faTimes"></fa-icon>
          </button>
        </ng-container>
      </div>
      <div class="node-details__hint"
           *ngIf="isLinkingUser && !isLoadingUsers && associatableUsers.length === 0">
        No users available to link.
      </div>
    </div>

  </div>

  <!-- Children (collapsible) -->
  <div class="node-children" *ngIf="isExpanded">

    <!-- New child input row -->
    <div class="add-child-row" *ngIf="isAddingChild">
      <span class="toggle-spacer"></span>
      <input #addInputEl
             class="add-input"
             [(ngModel)]="newChildName"
             (keyup.enter)="saveAddChild()"
             (keyup.escape)="cancelAddChild()"
             placeholder="New node name…"
             [disabled]="isSaving" />
      <div class="node-actions node-actions--visible">
        <button class="action-btn action-btn--save"
                (click)="saveAddChild()"
                title="Add node"
                [disabled]="isSaving || !newChildName.trim()">
          <fa-icon [icon]="faCheck"></fa-icon>
        </button>
        <button class="action-btn action-btn--cancel"
                (click)="cancelAddChild()"
                title="Cancel"
                [disabled]="isSaving">
          <fa-icon [icon]="faTimes"></fa-icon>
        </button>
      </div>
    </div>

    <!-- Recursive child nodes -->
    <app-node-tree-item
      *ngFor="let child of node.childNodes"
      [node]="child"
      [customerId]="customerId"
      [isRoot]="false"
      [level]="level + 1"
      [geoAssets]="geoAssets"
      (nodeChanged)="onChildChanged($event)">
    </app-node-tree-item>

  </div>

</div>
